import { FormikErrors, useFormikContext } from "formik";
import { TFunction } from "i18next";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { EventAddressFormValues, EventFormValues } from "../../types/event";
import { EventAddressStepFormFields } from "./EventAddressStepFormFields";
import { EventFormCard } from "./EventFormCard";
import { getPrevLink, useSubmitEventStep } from "./eventStepperUtils";

export function getEventAddressStepValidator(t: TFunction) {
	return (values: EventAddressFormValues): FormikErrors<EventAddressFormValues> => {
		const errors: FormikErrors<EventFormValues> = {};
		if (
			values.eventType?.addressRequirement === "required" ||
			values.address?.city ||
			values.address?.zip ||
			values.address?.country ||
			values.address?.houseNumber ||
			values.address?.street ||
			(values.eventType?.addressRequirement === "optional" &&
				values.address?.additionalInfo &&
				(!values.address?.city ||
					!values.address?.zip ||
					!values.address?.country ||
					!values.address?.houseNumber ||
					!values.address?.street))
		) {
			const addressErrors: FormikErrors<EventAddressFormValues>["address"] = {};

			if (!values.address?.city) {
				addressErrors.city = t("Field is required");
			}
			if (!values.address?.zip) {
				addressErrors.zip = t("Field is required");
			}
			if (!values.address?.street) {
				addressErrors.street = t("Field is required");
			}
			if (!values.address?.houseNumber) {
				addressErrors.houseNumber = t("Field is required");
			}
			if (!values.address?.country) {
				addressErrors.country = t("Field is required");
			}

			if (Object.keys(addressErrors).length > 0) {
				errors.address = addressErrors;
			}
		}
		return errors;
	};
}

export const isEventAddressStepValid = (errors: FormikErrors<EventAddressFormValues>) => {
	return !errors.address;
};

interface Props {
	variant?: "elevated" | "outlined";
}

export const EventAddressStep: FC<Props> = ({ variant }) => {
	const { t } = useTranslation();
	const { values, setTouched, touched } = useFormikContext<EventAddressFormValues>();
	const submitEventStep = useSubmitEventStep();
	const handleNextClick = useCallback(async () => {
		const errors = await setTouched({
			...touched,
			address: {
				city: true,
				zip: true,
				street: true,
				houseNumber: true,
				country: true,
				additionalInfo: true,
			},
		});

		if (errors && isEventAddressStepValid(errors)) {
			await submitEventStep(values.eventType, "address");
		}
	}, [setTouched, submitEventStep, touched, values.eventType]);

	return (
		<EventFormCard
			variant={variant}
			title={t("Address")}
			onNextClick={handleNextClick}
			showLanguageSwitcher={false}
			prevLink={getPrevLink(values.eventType, "address")}
			canClearAddress={true}
		>
			<EventAddressStepFormFields />
		</EventFormCard>
	);
};
